import { css, html, LitElement, PropertyValues, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import {
  CountryCode,
  isValidPhoneNumber,
  parsePhoneNumber,
  getExampleNumber,
  parse,
} from 'libphonenumber-js';
import parseMobile from 'libphonenumber-js/mobile';
import examples from 'libphonenumber-js/examples.mobile.json';
import phonePlaceholder from './phone-placeholder.js';

/**
 * Phone inout molecule. Used in Front interfaces.
 * phoneInputValue -  string | undefined;
 * label -  string.
 * customStyle - string.
 * placeholder -  string|undefined;
 *
 */
interface InputCountry {
  countryValue: CountryCode;
  countryName?: string;
}

@customElement('nn-phone-input')
export class phoneInput extends LitElement {
  static styles = css`
    .phone-input-ctn {
      align-items: center;
      display: flex;
      flex-direction: row;
      gap: var(--nn-phone-input_phone-input-ctn_gap, 5px);
    }
    .phone-input-ctn .select {
      height: var(--nn-phone-input_phone-input-ctn_select_height, 32px);
      padding: var(--nn-phone-input_phone-input-ctn_select_padding, 5px);
      width: var(--nn-phone-input_phone-input-ctn_select_width, 48px);
    }
    .phone-input-ctn .select:hover {
      cursor: pointer;
      background: var(
        --nn-phone-input_phone-input-ctn_select--hover_backgrounf,
        #e1e1e1
      );
    }
    .phone-input-ctn.disabled .select:hover {
      cursor: default;
      background: transparent;
    }
    .nn-phone-input .overlay.hidden {
      display: none;
    }
    .nn-phone-input .overlay {
      background: var(--nn-phone-input_overlay_background, #fff);
      border: 1px solid var(--nn-phone-input_overlay_border-color, #808080);
      position: absolute;
    }
    .nn-phone-input .overlay .search-country {
      border-bottom: 1px solid
        var(
          --nn-phone-input_overlay_search-country_border-bottom-color,
          #808080
        );
    }
    .nn-phone-input .overlay .search-country input {
      border-radius: 0;
      border: none;
      box-shadow: none;
      outline: none;
      padding: var(--nn-phone-input_overlay_search-country_input_padding, 8px);
      width: calc(
        100% - 2 *
          var(--nn-phone-input_overlay_search-country_input_padding, 8px)
      );
    }
    .nn-phone-input .overlay .options {
      max-height: var(--nn-phone-input_overlay_options_max_height, 400px);
      overflow: auto;
    }
    .nn-phone-input .overlay .options .option {
      align-items: center;
      display: flex;
      flex-direction: row;
      gap: var(--nn-phone-input_overlay_options_option_gap, 6px);
      padding: var(--nn-phone-input_overlay_options_option_padding, 6px);
    }
    .nn-phone-input .overlay .options .option nn-flag {
      height: var(--nn-phone-input_overlay_options_option_flag_height, 32px);
      width: var(--nn-phone-input_overlay_options_option_flag_width, 48px);
    }
    .nn-phone-input .overlay .options .option.selected {
      background: var(
        --nn-phone-input_overlay_options_option-selected_background,
        #f6f6f6
      );
    }
    .nn-phone-input .overlay .options .option:hover {
      background: var(
        --nn-phone-input_overlay_options_option--hover_background,
        #f0f0f0
      );
      cursor: pointer;
    }
  `;

  @property({ type: String }) phoneInputValue: string | undefined = '';

  @property({ type: String }) label: string = '';

  @property({ type: String }) customStyle: string = '';

  @property({ type: String }) placeholder: string | undefined;

  @property({ type: Boolean }) disabled: boolean = false;

  @property({ type: Array }) countries: InputCountry[] = [
    { countryName: 'France', countryValue: 'FR' },
    { countryName: 'Afghanistan', countryValue: 'AF' },
    { countryName: 'land Islands', countryValue: 'AX' },
    { countryName: 'Albania', countryValue: 'AL' },
    { countryName: 'Algeria', countryValue: 'DZ' },
    { countryName: 'American Samoa', countryValue: 'AS' },
    { countryName: 'Andorra', countryValue: 'AD' },
    { countryName: 'Angola', countryValue: 'AO' },
    { countryName: 'Anguilla', countryValue: 'AI' },
    { countryName: 'Antigua and Barbuda', countryValue: 'AG' },
    { countryName: 'Argentina', countryValue: 'AR' },
    { countryName: 'Armenia', countryValue: 'AM' },
    { countryName: 'Aruba', countryValue: 'AW' },
    { countryName: 'Australia', countryValue: 'AU' },
    { countryName: 'Austria', countryValue: 'AT' },
    { countryName: 'Azerbaijan', countryValue: 'AZ' },
    { countryName: 'Bahamas', countryValue: 'BS' },
    { countryName: 'Bahrain', countryValue: 'BH' },
    { countryName: 'Bangladesh', countryValue: 'BD' },
    { countryName: 'Barbados', countryValue: 'BB' },
    { countryName: 'Belarus', countryValue: 'BY' },
    { countryName: 'Belgium', countryValue: 'BE' },
    { countryName: 'Belize', countryValue: 'BZ' },
    { countryName: 'Benin', countryValue: 'BJ' },
    { countryName: 'Bermuda', countryValue: 'BM' },
    { countryName: 'Bhutan', countryValue: 'BT' },
    { countryName: 'Bolivia', countryValue: 'BO' },
    { countryName: 'Bosnia and Herzegovina', countryValue: 'BA' },
    { countryName: 'Botswana', countryValue: 'BW' },
    { countryName: 'Brazil', countryValue: 'BR' },
    { countryName: 'British Indian Ocean Territory', countryValue: 'IO' },
    { countryName: 'Brunei Darussalam', countryValue: 'BN' },
    { countryName: 'Bulgaria', countryValue: 'BG' },
    { countryName: 'Burkina Faso', countryValue: 'BF' },
    { countryName: 'Burundi', countryValue: 'BI' },
    { countryName: 'Cambodia', countryValue: 'KH' },
    { countryName: 'Cameroon', countryValue: 'CM' },
    { countryName: 'Canada', countryValue: 'CA' },
    { countryName: 'Cape Verde', countryValue: 'CV' },
    { countryName: 'Cayman Islands', countryValue: 'KY' },
    { countryName: 'Central African Republic', countryValue: 'CF' },
    { countryName: 'Chad', countryValue: 'TD' },
    { countryName: 'Chile', countryValue: 'CL' },
    { countryName: 'China', countryValue: 'CN' },
    { countryName: 'Christmas Island', countryValue: 'CX' },
    { countryName: 'Cocos (Keeling) Islands', countryValue: 'CC' },
    { countryName: 'Colombia', countryValue: 'CO' },
    { countryName: 'Comoros', countryValue: 'KM' },
    { countryName: 'Congo', countryValue: 'CG' },
    {
      countryName: 'Congo, The Democratic Republic of the',
      countryValue: 'CD',
    },
    { countryName: 'Cook Islands', countryValue: 'CK' },
    { countryName: 'Costa Rica', countryValue: 'CR' },
    { countryName: "Cote D'Ivoire", countryValue: 'CI' },
    { countryName: 'Croatia', countryValue: 'HR' },
    { countryName: 'Cuba', countryValue: 'CU' },
    { countryName: 'Cyprus', countryValue: 'CY' },
    { countryName: 'Czech Republic', countryValue: 'CZ' },
    { countryName: 'Denmark', countryValue: 'DK' },
    { countryName: 'Djibouti', countryValue: 'DJ' },
    { countryName: 'Dominica', countryValue: 'DM' },
    { countryName: 'Dominican Republic', countryValue: 'DO' },
    { countryName: 'Ecuador', countryValue: 'EC' },
    { countryName: 'Egypt', countryValue: 'EG' },
    { countryName: 'El Salvador', countryValue: 'SV' },
    { countryName: 'Equatorial Guinea', countryValue: 'GQ' },
    { countryName: 'Eritrea', countryValue: 'ER' },
    { countryName: 'Estonia', countryValue: 'EE' },
    { countryName: 'Ethiopia', countryValue: 'ET' },
    { countryName: 'Falkland Islands (Malvinas)', countryValue: 'FK' },
    { countryName: 'Faroe Islands', countryValue: 'FO' },
    { countryName: 'Fiji', countryValue: 'FJ' },
    { countryName: 'Finland', countryValue: 'FI' },
    { countryName: 'French Guiana', countryValue: 'GF' },
    { countryName: 'French Polynesia', countryValue: 'PF' },
    { countryName: 'Gabon', countryValue: 'GA' },
    { countryName: 'Gambia', countryValue: 'GM' },
    { countryName: 'Georgia', countryValue: 'GE' },
    { countryName: 'Germany', countryValue: 'DE' },
    { countryName: 'Ghana', countryValue: 'GH' },
    { countryName: 'Gibraltar', countryValue: 'GI' },
    { countryName: 'Greece', countryValue: 'GR' },
    { countryName: 'Greenland', countryValue: 'GL' },
    { countryName: 'Grenada', countryValue: 'GD' },
    { countryName: 'Guadeloupe', countryValue: 'GP' },
    { countryName: 'Guam', countryValue: 'GU' },
    { countryName: 'Guatemala', countryValue: 'GT' },
    { countryName: 'Guernsey', countryValue: 'GG' },
    { countryName: 'Guinea', countryValue: 'GN' },
    { countryName: 'Guinea-Bissau', countryValue: 'GW' },
    { countryName: 'Guyana', countryValue: 'GY' },
    { countryName: 'Haiti', countryValue: 'HT' },
    { countryName: 'Holy See (Vatican City State)', countryValue: 'VA' },
    { countryName: 'Honduras', countryValue: 'HN' },
    { countryName: 'Hong Kong', countryValue: 'HK' },
    { countryName: 'Hungary', countryValue: 'HU' },
    { countryName: 'Iceland', countryValue: 'IS' },
    { countryName: 'India', countryValue: 'IN' },
    { countryName: 'Indonesia', countryValue: 'ID' },
    { countryName: 'Iran, Islamic Republic Of', countryValue: 'IR' },
    { countryName: 'Iraq', countryValue: 'IQ' },
    { countryName: 'Ireland', countryValue: 'IE' },
    { countryName: 'Isle of Man', countryValue: 'IM' },
    { countryName: 'Israel', countryValue: 'IL' },
    { countryName: 'Italy', countryValue: 'IT' },
    { countryName: 'Jamaica', countryValue: 'JM' },
    { countryName: 'Japan', countryValue: 'JP' },
    { countryName: 'Jersey', countryValue: 'JE' },
    { countryName: 'Jordan', countryValue: 'JO' },
    { countryName: 'Kazakhstan', countryValue: 'KZ' },
    { countryName: 'Kenya', countryValue: 'KE' },
    { countryName: 'Kiribati', countryValue: 'KI' },
    {
      countryName: "Korea, Democratic People'S Republic of",
      countryValue: 'KP',
    },
    { countryName: 'Korea, Republic of', countryValue: 'KR' },
    { countryName: 'Kuwait', countryValue: 'KW' },
    { countryName: 'Kyrgyzstan', countryValue: 'KG' },
    { countryName: "Lao People'S Democratic Republic", countryValue: 'LA' },
    { countryName: 'Latvia', countryValue: 'LV' },
    { countryName: 'Lebanon', countryValue: 'LB' },
    { countryName: 'Lesotho', countryValue: 'LS' },
    { countryName: 'Liberia', countryValue: 'LR' },
    { countryName: 'Libyan Arab Jamahiriya', countryValue: 'LY' },
    { countryName: 'Liechtenstein', countryValue: 'LI' },
    { countryName: 'Lithuania', countryValue: 'LT' },
    { countryName: 'Luxembourg', countryValue: 'LU' },
    { countryName: 'Macao', countryValue: 'MO' },
    {
      countryName: 'Macedonia, The Former Yugoslav Republic of',
      countryValue: 'MK',
    },
    { countryName: 'Madagascar', countryValue: 'MG' },
    { countryName: 'Malawi', countryValue: 'MW' },
    { countryName: 'Malaysia', countryValue: 'MY' },
    { countryName: 'Maldives', countryValue: 'MV' },
    { countryName: 'Mali', countryValue: 'ML' },
    { countryName: 'Malta', countryValue: 'MT' },
    { countryName: 'Marshall Islands', countryValue: 'MH' },
    { countryName: 'Martinique', countryValue: 'MQ' },
    { countryName: 'Mauritania', countryValue: 'MR' },
    { countryName: 'Mauritius', countryValue: 'MU' },
    { countryName: 'Mayotte', countryValue: 'YT' },
    { countryName: 'Mexico', countryValue: 'MX' },
    { countryName: 'Micronesia, Federated States of', countryValue: 'FM' },
    { countryName: 'Moldova, Republic of', countryValue: 'MD' },
    { countryName: 'Monaco', countryValue: 'MC' },
    { countryName: 'Mongolia', countryValue: 'MN' },
    { countryName: 'Montenegro', countryValue: 'ME' },
    { countryName: 'Montserrat', countryValue: 'MS' },
    { countryName: 'Morocco', countryValue: 'MA' },
    { countryName: 'Mozambique', countryValue: 'MZ' },
    { countryName: 'Myanmar', countryValue: 'MM' },
    { countryName: 'Namibia', countryValue: 'NA' },
    { countryName: 'Nauru', countryValue: 'NR' },
    { countryName: 'Nepal', countryValue: 'NP' },
    { countryName: 'Netherlands', countryValue: 'NL' },
    { countryName: 'New Caledonia', countryValue: 'NC' },
    { countryName: 'New Zealand', countryValue: 'NZ' },
    { countryName: 'Nicaragua', countryValue: 'NI' },
    { countryName: 'Niger', countryValue: 'NE' },
    { countryName: 'Nigeria', countryValue: 'NG' },
    { countryName: 'Niue', countryValue: 'NU' },
    { countryName: 'Norfolk Island', countryValue: 'NF' },
    { countryName: 'Northern Mariana Islands', countryValue: 'MP' },
    { countryName: 'Norway', countryValue: 'NO' },
    { countryName: 'Oman', countryValue: 'OM' },
    { countryName: 'Pakistan', countryValue: 'PK' },
    { countryName: 'Palau', countryValue: 'PW' },
    { countryName: 'Palestinian Territory, Occupied', countryValue: 'PS' },
    { countryName: 'Panama', countryValue: 'PA' },
    { countryName: 'Papua New Guinea', countryValue: 'PG' },
    { countryName: 'Paraguay', countryValue: 'PY' },
    { countryName: 'Peru', countryValue: 'PE' },
    { countryName: 'Philippines', countryValue: 'PH' },
    { countryName: 'Poland', countryValue: 'PL' },
    { countryName: 'Portugal', countryValue: 'PT' },
    { countryName: 'Puerto Rico', countryValue: 'PR' },
    { countryName: 'Qatar', countryValue: 'QA' },
    { countryName: 'Reunion', countryValue: 'RE' },
    { countryName: 'Romania', countryValue: 'RO' },
    { countryName: 'Russian Federation', countryValue: 'RU' },
    { countryName: 'RWANDA', countryValue: 'RW' },
    { countryName: 'Saint Helena', countryValue: 'SH' },
    { countryName: 'Saint Kitts and Nevis', countryValue: 'KN' },
    { countryName: 'Saint Lucia', countryValue: 'LC' },
    { countryName: 'Saint Pierre and Miquelon', countryValue: 'PM' },
    { countryName: 'Saint Vincent and the Grenadines', countryValue: 'VC' },
    { countryName: 'Samoa', countryValue: 'WS' },
    { countryName: 'San Marino', countryValue: 'SM' },
    { countryName: 'Sao Tome and Principe', countryValue: 'ST' },
    { countryName: 'Saudi Arabia', countryValue: 'SA' },
    { countryName: 'Senegal', countryValue: 'SN' },
    { countryName: 'Serbia', countryValue: 'RS' },
    { countryName: 'Seychelles', countryValue: 'SC' },
    { countryName: 'Sierra Leone', countryValue: 'SL' },
    { countryName: 'Singapore', countryValue: 'SG' },
    { countryName: 'Slovakia', countryValue: 'SK' },
    { countryName: 'Slovenia', countryValue: 'SI' },
    { countryName: 'Solomon Islands', countryValue: 'SB' },
    { countryName: 'Somalia', countryValue: 'SO' },
    { countryName: 'South Africa', countryValue: 'ZA' },
    { countryName: 'Spain', countryValue: 'ES' },
    { countryName: 'Sri Lanka', countryValue: 'LK' },
    { countryName: 'Sudan', countryValue: 'SD' },
    { countryName: 'Suriname', countryValue: 'SR' },
    { countryName: 'Svalbard and Jan Mayen', countryValue: 'SJ' },
    { countryName: 'Swaziland', countryValue: 'SZ' },
    { countryName: 'Sweden', countryValue: 'SE' },
    { countryName: 'Switzerland', countryValue: 'CH' },
    { countryName: 'Syrian Arab Republic', countryValue: 'SY' },
    { countryName: 'Taiwan, Province of China', countryValue: 'TW' },
    { countryName: 'Tajikistan', countryValue: 'TJ' },
    { countryName: 'Tanzania, United Republic of', countryValue: 'TZ' },
    { countryName: 'Thailand', countryValue: 'TH' },
    { countryName: 'Timor-Leste', countryValue: 'TL' },
    { countryName: 'Togo', countryValue: 'TG' },
    { countryName: 'Tokelau', countryValue: 'TK' },
    { countryName: 'Tonga', countryValue: 'TO' },
    { countryName: 'Trinidad and Tobago', countryValue: 'TT' },
    { countryName: 'Tunisia', countryValue: 'TN' },
    { countryName: 'Turkey', countryValue: 'TR' },
    { countryName: 'Turkmenistan', countryValue: 'TM' },
    { countryName: 'Turks and Caicos Islands', countryValue: 'TC' },
    { countryName: 'Tuvalu', countryValue: 'TV' },
    { countryName: 'Uganda', countryValue: 'UG' },
    { countryName: 'Ukraine', countryValue: 'UA' },
    { countryName: 'United Arab Emirates', countryValue: 'AE' },
    { countryName: 'United Kingdom', countryValue: 'GB' },
    { countryName: 'United States', countryValue: 'US' },
    { countryName: 'Uruguay', countryValue: 'UY' },
    { countryName: 'Uzbekistan', countryValue: 'UZ' },
    { countryName: 'Vanuatu', countryValue: 'VU' },
    { countryName: 'Venezuela', countryValue: 'VE' },
    { countryName: 'Viet Nam', countryValue: 'VN' },
    { countryName: 'Virgin Islands, British', countryValue: 'VG' },
    { countryName: 'Virgin Islands, U.S.', countryValue: 'VI' },
    { countryName: 'Wallis and Futuna', countryValue: 'WF' },
    { countryName: 'Western Sahara', countryValue: 'EH' },
    { countryName: 'Yemen', countryValue: 'YE' },
    { countryName: 'Zambia', countryValue: 'ZM' },
    { countryName: 'Zimbabwe', countryValue: 'ZW' },
  ];

  // Default country if not provided is FR
  @property({ type: String }) countryValue: CountryCode = 'FR';

  @property({ type: String }) language:
    | 'fr-FR'
    | 'en-EN'
    | 'be-FR'
    | 'be-EN'
    | 'fr-BE'
    | 'en-BE' = 'fr-FR';

  openState = false;

  filteredCountries = this.countries;

  protected firstUpdated(_changedProperties: PropertyValues) {
    // eslint-disable-next-line wc/guard-super-call
    super.firstUpdated(_changedProperties);

    if (this.phoneInputValue !== undefined) {
      this.changeCountryFlagByPhone();
    }

    // on init, we will check the validity of the phone number in input
    setTimeout(() => {
      this.checkNumberValidity();
    }, 100);
  }

  protected updated(_changedProperties: PropertyValues) {
    // eslint-disable-next-line wc/guard-super-call
    super.updated(_changedProperties);

    if (_changedProperties.has('phoneInputValue')) {
      this.changeCountryFlagByPhone();
    }
  }

  changeCountryFlagByPhone() {
    try {
      // on load : parse the phone
      const nationalPhoneNumber = parsePhoneNumber(
        this.pad(this.phoneInputValue)
      );

      // eslint-disable-next-line prefer-destructuring
      this.phoneInputValue = nationalPhoneNumber
        .formatInternational()
        .replace(/\s/gm, '');

      // detect country of phone number to set the HTML select and countryValue
      this.countryValue =
        nationalPhoneNumber.country || this.countries[0].countryValue;
    } catch (e) {
      // if parse fails because it's NOT_A_NUMBER, don't throw anything
    }
  }

  _onChangeCountry(country: CountryCode): void {
    this._closeSelect();
    this.countryValue = country;
    this.checkNumberValidity();

    // set focus on the <input>
    (this.shadowRoot?.querySelector('#phone') as HTMLElement).focus();
  }

  _toggleState() {
    if (this.openState) {
      this._closeSelect();
    } else if (!this.disabled) {
      this._openSelect();
    }
  }

  _closeSelect() {
    this.openState = false;
    this.shadowRoot?.querySelector('.overlay')?.classList.add('hidden');
    this.shadowRoot
      ?.querySelector('#country-select')
      ?.classList.remove('opened');

    // reset the filters on close
    this._resetFilters();
  }

  _openSelect() {
    this.openState = true;
    this.shadowRoot?.querySelector('.overlay')?.classList.remove('hidden');
    this.shadowRoot?.querySelector('#country-select')?.classList.add('opened');

    // the focus will not work without a little delay... :(
    setTimeout(
      () =>
        (
          this.shadowRoot?.querySelector('#search-country') as HTMLInputElement
        ).focus(),
      50
    );
  }

  _onChangePhone(e: Event): void {
    e.stopPropagation();
    const target = e.target as HTMLInputElement;
    this.phoneInputValue = target.value;

    if (this.openState) {
      this._closeSelect();
    }

    const detectedCountry = parse(
      `+${target.value.replace('+', '') || '0000'}` // if no value then set a lot of 0 to avoid parse errors
    ).country;

    // if the input detect a different country between the select and the input then update countryValue
    if (detectedCountry && detectedCountry !== this.countryValue) {
      this.countryValue = detectedCountry;
    }

    this.checkNumberValidity();
  }

  getPlaceHolder(): string {
    if (this.placeholder) {
      return this.placeholder;
    }
    return (
      getExampleNumber(this.countryValue, examples)?.formatNational() || ''
    );
  }

  pad = (value: string | number | undefined): string =>
    value && value.toString().length > 4
      ? value.toString()
      : (value?.toString() || '').padEnd(4, '0');

  _filterCountries(e: KeyboardEvent) {
    e.stopPropagation();
    e.preventDefault();

    // on Escape close the filter
    if (e.keyCode === 27) {
      this._closeSelect();
      return;
    }

    const searchValue = (<HTMLInputElement>e.target).value;

    if (searchValue === '') {
      this._resetFilters();
      return;
    }

    this.filteredCountries = this.countries.filter(
      filter =>
        filter.countryName?.toLowerCase().includes(searchValue.toLowerCase()) ||
        filter.countryValue.toLowerCase().includes(searchValue.toLowerCase())
    );

    // on Enter select the first occurence of filter
    if (e.keyCode === 13) {
      this._onChangeCountry(this.filteredCountries[0].countryValue);
      this._closeSelect();
      return;
    }

    this.requestUpdate();
  }

  _resetFilters() {
    this.filteredCountries = this.countries;
    (
      this.shadowRoot
        ?.querySelector('.overlay')
        ?.querySelector('input') as HTMLInputElement
    ).value = '';
    this.requestUpdate();
  }

  checkNumberValidity(): void {
    const phoneInputPad = this.pad(this.phoneInputValue);
    let isValid = isValidPhoneNumber(phoneInputPad, this.countryValue);
    const isMobile =
      parseMobile(phoneInputPad, this.countryValue)?.getType() !== undefined;

    if (isValid) {
      this.shadowRoot?.querySelector('#phone')?.classList.remove('invalid');
    } else {
      this.shadowRoot?.querySelector('#phone')?.classList.add('invalid');
    }

    const parsedPhoneNumber =
      isValid && parsePhoneNumber(phoneInputPad, this.countryValue);

    if (parsedPhoneNumber) {
      isValid =
        this.countryValue === parsedPhoneNumber?.country ? isValid : false;
    }
    // on check validity send an event with validator errors
    this.dispatchEvent(
      new CustomEvent('sendResultData', {
        detail: {
          isValid,
          isMobile,
          phoneInput: this.phoneInputValue || '',
          internationalFormattedNumber: parsedPhoneNumber
            ? parsedPhoneNumber.formatInternational()
            : '',
          countryCallingCode: parsedPhoneNumber
            ? parsedPhoneNumber.countryCallingCode
            : '',
          country: this.countryValue,
        },
      })
    );

    setTimeout(() => this.requestUpdate(), 50);
  }

  render() {
    return html`
      <style>
        :host {
          --nn-flag_span_img_width: var(--nn-phone-input_overlay_options_option_flag_width, 48px);
        }
        ${unsafeCSS(this.customStyle)}
      </style>
      <div class="nn-phone-input">
        <span class="label">${this.label}</span>
        <label class="phone-input-ctn${this.disabled ? ' disabled' : ''}">
          <div
            name="country"
            id="country-select"
            class="select"
            @click=${() => this._toggleState()}
            @keydown=${() => this._toggleState()}
          >
            <nn-flag
              .country="${this.countryValue}"
              country="${this.countryValue}"
            ></nn-flag>
          </div>
          <div class="input-ctn">
            <input
              @input="${(e: Event) => this._onChangePhone(e)}"
              @keyup="${(e: Event) => e.stopPropagation()}"
              .value="${this.phoneInputValue}"
              .placeholder="${this.getPlaceHolder()}"
              .disabled="${this.disabled}"
              type="text"
              id="phone"
              name="number"
              required
            />
          </div>
        </label>
        <div class="overlay hidden">
          <div class="search-country">
            <input
              type="text"
              placeholder=${phonePlaceholder[this.language]}
              id="search-country"
              @keyup=${(e: KeyboardEvent) => this._filterCountries(e)}
            />
          </div>
          <div class="options">
            ${this.filteredCountries.map(
              country =>
                html`<div
                  class="option${this.countryValue === country.countryValue
                    ? ' selected'
                    : ''}"
                  @click=${() => this._onChangeCountry(country.countryValue)}
                  @keydown=${() => this._toggleState()}
                >
                  <nn-flag
                    .country="${country.countryValue}"
                    country="${country.countryValue}"
                  ></nn-flag>
                  ${country.countryName || country.countryValue}
                </div>`
            )}
          </div>
        </div>
      </div>
    `;
  }
}
